import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  ServerResolutionResult,
  TransferTableValidateDestinationPositionFillInPlateQuery,
  TransferTableValidateDestinationPositionForCreateContainerQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {
  InvalidPositionFormatError,
  NoPositionError,
  parseGridPosition,
  PositionOutOfBoundsError,
} from 'samples/SampleUtil';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import APIUtil from 'util/APIUtil';
import {assertNever} from 'util/Types';

import {
  GetDestinationBoxCellsQuery,
  GetDestinationBoxCellsQueryVariables,
} from './__generated/GetDestinationBoxCellsQuery';
import {
  GetDestinationMatrixPlateCellsQuery,
  GetDestinationMatrixPlateCellsQueryVariables,
} from './__generated/GetDestinationMatrixPlateCellsQuery';
import {
  GetDestinationPlateCellsQuery,
  GetDestinationPlateCellsQueryVariables,
} from './__generated/GetDestinationPlateCellsQuery';
import extractStorableId from './extractStorableId';

const GET_DESTINATION_BOX_CELLS_QUERY = gql`
  query GetDestinationBoxCellsQuery($boxIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    storables(ids: $boxIds) {
      ... on BoxNode {
        id
        cells {
          ... on GridCellNode {
            container {
              ... on ContainerNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
            rowIndex
            columnIndex
          }
        }
        storableSchema {
          ... on BoxSchemaNode {
            id
            height
            width
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const GET_DESTINATION_MATRIX_PLATE_CELLS_QUERY = gql`
  query GetDestinationMatrixPlateCellsQuery($plateIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $plateIds) {
      ... on PlateNode {
        id
        cells {
          ... on GridCellNode {
            container {
              ... on ContainerNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
            rowIndex
            columnIndex
          }
        }
        storableSchema {
          ... on PlateSchemaNode {
            id
            height
            width
            plateSchemaType
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const GET_DESTINATION_PLATE_CELLS_QUERY = gql`
  query GetDestinationPlateCellsQuery($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    storables(ids: $plateIds) {
      ... on PlateNode {
        id
        cells {
          ... on GridCellNode {
            container {
              ... on ContainerNode {
                id
                contents {
                  id
                  containedItem {
                    ... on WorklistItemInterface {
                      id
                    }
                    ...InaccessibleInfoGraphQL
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
            rowIndex
            columnIndex
          }
        }
        storableSchema {
          ... on PlateSchemaNode {
            id
            height
            width
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doBoxQuery(boxId: string): Promise<GetDestinationBoxCellsQuery> {
    return await doQueryWithoutCache<GetDestinationBoxCellsQuery, GetDestinationBoxCellsQueryVariables>(
      {query: GET_DESTINATION_BOX_CELLS_QUERY},
      {
        variables: {boxIds: [boxId]},
      }
    );
  },

  async doMatrixPlateQuery(plateId: string): Promise<GetDestinationMatrixPlateCellsQuery> {
    return await doQueryWithoutCache<
      GetDestinationMatrixPlateCellsQuery,
      GetDestinationMatrixPlateCellsQueryVariables
    >(
      {query: GET_DESTINATION_MATRIX_PLATE_CELLS_QUERY},
      {
        variables: {plateIds: [plateId]},
      }
    );
  },

  async doPlateQuery(plateId: string): Promise<GetDestinationPlateCellsQuery> {
    return await doQueryWithoutCache<GetDestinationPlateCellsQuery, GetDestinationPlateCellsQueryVariables>(
      {query: GET_DESTINATION_PLATE_CELLS_QUERY},
      {
        variables: {plateIds: [plateId]},
      }
    );
  },
};

function getFriendlyName(gridType: 'BOX' | 'MATRIX_PLATE'): string {
  if (gridType === 'BOX') {
    return 'box';
  } else if (gridType === 'MATRIX_PLATE') {
    return 'matrix plate';
  } else {
    throw new Error(`Unexpected grid type ${assertNever(gridType)}`);
  }
}

export const DestinationPositionResolver = {
  /**
   * Handle resolution and validation for user's inputted position in the destination box or matrix plate.
   * Only used for transfer table's CREATE mode.
   */
  validateDestinationPositionForBoxOrMatrixPlate: async (
    query: TransferTableValidateDestinationPositionForCreateContainerQuery
  ): Promise<ServerResolutionResult> => {
    const {gridType, boxOrPlateId, position} = query;
    let data;
    if (APIUtil.isBox(boxOrPlateId)) {
      data = await stubbableFunctions.doBoxQuery(boxOrPlateId);
    } else if (APIUtil.isPlate(boxOrPlateId)) {
      data = await stubbableFunctions.doMatrixPlateQuery(boxOrPlateId);
    } else {
      throw new Error('Not a box or plate');
    }
    const boxOrMatrixPlate = data.storables[0];

    if (isInaccessible(boxOrMatrixPlate)) {
      // If the box or plate is inaccessible, there's no point in validating the position.
      // The user should already know there's an issue, because the box or plate will be rendered as
      // a `(No access)` chip.
      return makeCustomResolutionResultFromTypedValue(null);
    }

    if (gridType === 'BOX') {
      assert(boxOrMatrixPlate.__typename === 'BoxNode', 'Expected a box');
    } else if (gridType === 'MATRIX_PLATE') {
      assert(boxOrMatrixPlate.__typename === 'PlateNode', 'Expected a plate');
      // @ts-expect-error: TypeScript doesn't understand that boxOrMatrixPlate is never a container
      if (boxOrMatrixPlate.storableSchema.plateSchemaType === PlateSchemaType.FIXED_PLATE) {
        // No need to validate position if the user entered a well plate instead of a matrix plate.
        // Instead, we will show a red dog ear on the plate itself.
        return makeCustomResolutionResultFromTypedValue(null);
      }
    } else {
      throw new Error(`Unexpected grid type ${assertNever(gridType)}`);
    }

    // Parse position
    let rowIndex;
    let columnIndex;
    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position,
        // @ts-expect-error: TypeScript doesn't understand that boxOrMatrixPlate is never a container
        gridHeight: boxOrMatrixPlate.storableSchema.height,
        // @ts-expect-error: TypeScript doesn't understand that boxOrMatrixPlate is never a container
        gridWidth: boxOrMatrixPlate.storableSchema.width,
      }));
    } catch (e) {
      // If there's an InvalidPositionError thrown, wrap in a ServerResolutionResult and return it
      if (e instanceof PositionOutOfBoundsError) {
        return {
          type: undefined,
          error: `Position does not exist in destination ${getFriendlyName(gridType)}`,
          resolvedValue: undefined,
        };
      } else if (e instanceof InvalidPositionFormatError) {
        return {
          type: undefined,
          error: 'Position must be in format: A1, A01, or 1',
          resolvedValue: undefined,
        };
      } else if (e instanceof NoPositionError) {
        // If the position is blank, customEval should have returned early,
        // and custom resolution shouldn't get called at all
        throw new Error('Position should not be null.');
      } else {
        throw e;
      }
    }

    // Check if the position is empty
    // @ts-expect-error: TypeScript doesn't understand that boxOrMatrixPlate is never a container
    if (boxOrMatrixPlate.cells[rowIndex][columnIndex] != null) {
      return {
        type: undefined,
        error: 'Position has existing container',
        resolvedValue: undefined,
      };
    }

    return makeCustomResolutionResultFromTypedValue(null);
  },

  /**
   * Handle resolution and validation for user's inputted position in the destination plate.
   * Only used for transfer table's FILL mode.
   */
  validateDestinationPlatePosition: async (
    query: TransferTableValidateDestinationPositionFillInPlateQuery
  ): Promise<ServerResolutionResult> => {
    const {plateId, position} = query;
    const data = await stubbableFunctions.doPlateQuery(plateId);
    const plate = data.storables[0];
    if (isInaccessible(plate)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    assert(plate.__typename === 'PlateNode', 'Expected a plate');

    // Parse position
    let rowIndex;
    let columnIndex;
    try {
      assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Should be a plate schema');
      ({rowIndex, columnIndex} = parseGridPosition({
        position,
        gridHeight: plate.storableSchema.height,
        gridWidth: plate.storableSchema.width,
      }));
    } catch (e) {
      // If there's an InvalidPositionError thrown, wrap in a ServerResolutionResult and return it
      if (e instanceof PositionOutOfBoundsError) {
        return {
          type: undefined,
          error: 'Position does not exist in destination plate',
          resolvedValue: undefined,
        };
      } else if (e instanceof InvalidPositionFormatError) {
        return {
          type: undefined,
          error: 'Position must be in format: A1, A01, or 1',
          resolvedValue: undefined,
        };
      } else if (e instanceof NoPositionError) {
        // If the position is blank, customEval should have returned early,
        // and custom resolution shouldn't get called at all
        throw new Error('Position should not be null.');
      } else {
        throw e;
      }
    }

    if (plate.cells[rowIndex][columnIndex] == null) {
      // we should never reach here if the plate is a well plate since this function is for the FILL mode.
      // However, a matrix plate could be wrongly entered in the table. In cases where there isn't a
      // container at this position of a matrix plate, we should error properly.
      return {
        type: undefined,
        error: 'There is no container at this position',
        resolvedValue: undefined,
      };
    }
    const destinationWell = plate.cells[rowIndex][columnIndex]!.container;
    if (isInaccessible(destinationWell)) {
      return {
        type: undefined,
        error: 'Well is inaccessible',
        resolvedValue: undefined,
      };
    }

    // Check if plate's well contents are empty (plate cells will never be null)
    if (destinationWell.contents.length !== 0) {
      return {
        type: undefined,
        error: 'Position has already been filled',
        resolvedValue: undefined,
      };
    }

    return makeCustomResolutionResultFromTypedValue(null);
  },
};

export function destinationBoxPositionCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  [boxTypedValue]: [TypedValue | null],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  if (isTypedValueEmpty(typedValue)) {
    return {val: typedValue, error: null, locked: false};
  }

  const position = formatTypedValue(typedValue);
  const boxId = extractStorableId(boxTypedValue);

  if (boxId == null || !APIUtil.isBox(boxId)) {
    return {val: {type: BaseTypedValueType.STRING, rawValue: position}, error: null, locked: false};
  }

  const {resolvedValue, error: resolutionError} = resolveFromResolutionQuery(
    {type: BaseTypedValueType.STRING, rawValue: position},
    {
      customResolutionQueryType:
        CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FOR_CREATE_CONTAINER,
      gridType: 'BOX',
      boxOrPlateId: boxId,
      position,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error: resolutionError, locked: false};
}

export function destinationMatrixPlatePositionCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  [plateTypedValue]: [TypedValue | null],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  if (isTypedValueEmpty(typedValue)) {
    return {val: typedValue, error: null, locked: false};
  }

  const position = formatTypedValue(typedValue);
  const plateId = extractStorableId(plateTypedValue);

  if (plateId == null || !APIUtil.isPlate(plateId)) {
    return {val: {type: BaseTypedValueType.STRING, rawValue: position}, error: null, locked: false};
  }

  const {resolvedValue, error: resolutionError} = resolveFromResolutionQuery(
    {type: BaseTypedValueType.STRING, rawValue: position},
    {
      customResolutionQueryType:
        CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FOR_CREATE_CONTAINER,
      gridType: 'MATRIX_PLATE',
      boxOrPlateId: plateId,
      position,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error: resolutionError, locked: false};
}

export function destinationPlatePositionCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  [plateTypedValue]: [TypedValue | null],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  if (isTypedValueEmpty(typedValue)) {
    return {val: typedValue, error: null, locked: false};
  }

  const position = formatTypedValue(typedValue);
  const plateId = extractStorableId(plateTypedValue);

  if (plateId == null || !APIUtil.isPlate(plateId)) {
    return {val: {type: BaseTypedValueType.STRING, rawValue: position}, error: null, locked: false};
  }

  const {resolvedValue, error: resolutionError} = resolveFromResolutionQuery(
    {type: BaseTypedValueType.STRING, rawValue: position},
    {
      customResolutionQueryType:
        CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FILL_IN_PLATE,
      plateId,
      position,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error: resolutionError, locked: false};
}
